import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AttributeValue,
  customAttributeOption,
} from '@app/shared/interfaces/Attribute';
import {
  GenericOdataResponse,
  OdataResponse,
} from '@app/shared/interfaces/odata-response';
import { Translation } from '@app/shared/interfaces/Translation';
import { AppConfigService } from '@app/shared/services/app-config.service';
import { ConnectionService } from '@app/shared/services/connection.service';
import { BehaviorSubject, map, Observable, of, tap, zip } from 'rxjs';
import { CompletionSet } from '../models/completion';
import { DropDownMetaData } from '../models/DropDownMetaData';
import { ObjectType } from '../models/Enums.enum';
import { DropDownOption } from '../models/GlobalInterfaces';
import { productTypes } from '@product/shared/product-base-logic';
import { ListPayload } from '../models/ListPayload';
import { Product } from '../models/product';
import { Language } from '@app/shared/interfaces/language';
@Injectable({
  providedIn: 'root',
})
export class CommonService {
  public static DefaultLanguageId = 0;
  defaultLang$ = new BehaviorSubject<DropDownOption>({
    Code: 0,
    Label: 'Default',
  });
  defaultLanguage = this.defaultLang$.asObservable();

  private _dropdownsMetaData: DropDownMetaData = {};
  get dropDownMetaData() {
    return this._dropdownsMetaData;
  }
  set dropDownMetaData(value) {
    this._dropdownsMetaData = value;
  }
  helperObject: { [key: string]: any } = {};

  constructor(
    private connectionService: ConnectionService,
    private http: HttpClient
  ) {}
  getTranslations(type: ObjectType, id: number): Observable<OdataResponse> {
    return new Observable((observer) => {
      this.connectionService.query(
        `${AppConfigService.settings.dataApiUrl}Translation`,
        0,
        0,
        '',
        '',
        [],
        {},
        (response) => {
          observer.next(response);
          observer.unsubscribe();
        }
      );
    });
  }
  getLanguages(): Observable<GenericOdataResponse<Language>> {
    return new Observable<GenericOdataResponse<Language>>((observer) => {
      this.connectionService.get(
        `${AppConfigService.settings.dataApiUrl}Language`,
        (response: GenericOdataResponse<Language>) => {
          observer.next(response);
          const defaultLanguage = response.value.find(
            (x) => x.IsDefault === true
          );
          if (defaultLanguage != null) {
            this.defaultLang$.next({
              Code: defaultLanguage.Id,
              Label: defaultLanguage.Name,
            });
            CommonService.DefaultLanguageId = defaultLanguage.Id;
          }
          observer.unsubscribe();
        }
      );
    });
  }

  saveTranslations(translations: Translation[]) {
    return this.http.post(
      `${AppConfigService.settings.apiUrl}CustomTranslation`,
      translations
    );
  }
  saveProductAttributesTransaltions(productAttributes: AttributeValue[]) {
    return this.http.post(
      `${AppConfigService.settings.apiUrl}CustomProductAttribute`,
      productAttributes
    );
  }
  getAttributesOptions(
    ids: number[]
  ): Observable<{ [key: number]: customAttributeOption[] }> {
    return this.http.post<{ [key: number]: customAttributeOption[] }>(
      `${AppConfigService.settings.apiUrl}CustomAttribute/GetAttributesOptions`,
      ids.map((x) => x.toString())
    );
  }
  getTags(): Observable<DropDownOption[]> {
    const options = this.dropDownMetaData['Tags'];
    if (options != null && options.length > 0) {
      return of(options);
    } else {
      return new Observable((observer) => {
        this.connectionService.allEntityData(
          `${AppConfigService.settings.dataApiUrl}Tag`,
          (response) => {
            const options = response.value.map((x) => {
              return { Code: x.Id, Label: x.Name };
            });
            this.dropDownMetaData['Tags'] = options;
            observer.next(options);
            observer.unsubscribe();
          }
        );
      });
    }
  }
  getAttributeSets(): Observable<DropDownOption[]> {
    const options = this.dropDownMetaData['AttributeSet'];
    if (options != null && options.length > 0) {
      return of(options);
    } else {
      return new Observable((observer) => {
        this.connectionService.allEntityData(
          `${AppConfigService.settings.dataApiUrl}AttributeSet`,
          (response) => {
            const options = response.value.map((x) => {
              return { Code: x.Id, Label: x.Name };
            });
            this.dropDownMetaData['AttributeSet'] = options;
            this.dropDownMetaData['AttributeSetId'] = options;
            observer.next(options);
            observer.unsubscribe();
          }
        );
      });
    }
  }
  getStatuses(): Observable<DropDownOption[]> {
    const options = this.dropDownMetaData['StatusId'];
    if (options != null && options.length > 0) {
      return of(options);
    } else {
      return new Observable((observer) => {
        this.connectionService.allEntityData(
          `${AppConfigService.settings.dataApiUrl}ProductStatus`,
          (response) => {
            const options = response.value.map((x) => {
              return { Code: x.Id, Label: x.Name };
            });
            this.dropDownMetaData['StatusId'] = options;
            observer.next(options);
            observer.unsubscribe();
          }
        );
      });
    }
  }
  getCompletionSets(): Observable<DropDownOption[]> {
    return this.http
      .get<CompletionSet[]>(
        `${AppConfigService.settings.apiUrl}CustomCompletion`
      )
      .pipe(
        map((response: CompletionSet[]) => {
          return response.map((x) => {
            const dropDownOption: DropDownOption = {
              Code: x.id,
              Label: x.title,
              Additional: x.completions,
            };
            return dropDownOption;
          });
        })
      );
  }
  getCategories() {
    return new Observable((obs) => {
      this.connectionService.get(
        `${AppConfigService.settings.dataApiUrl}Category?select=Id,Name`,
        (response: OdataResponse) => {
          const categories = response.value as { Id: number; Name: string }[];
          this.dropDownMetaData['categories'] = categories.map((x) => {
            return {
              Code: x.Id,
              Label: x.Name,
            };
          });
          obs.next(categories);
        }
      );
    });
  }
  getOptions() {
    const tagOptions$ = this.getTags();
    const attributeSetOprions$ = this.getAttributeSets();
    const statusOptions$ = this.getStatuses();
    const completionSet$ = this.getCompletionSets();
    const categories$ = this.getCategories();
    const inheritFrom$ = this.getCompletionSets();
    return zip(
      tagOptions$,
      attributeSetOprions$,
      statusOptions$,
      completionSet$,
      categories$,
      inheritFrom$
    ).pipe(
      tap((response) => {
        this.dropDownMetaData['Tags'] = response[0];
        this.dropDownMetaData['attributeSetId'] = response[1];
        this.dropDownMetaData['statusId'] = response[2];
        this.dropDownMetaData['completionSetId'] = response[3];
        this.dropDownMetaData['Type'] = productTypes;
        this.dropDownMetaData['inheritFrom'] = response[5];
      })
    );
  }
  getProducts(payload: ListPayload) {
    return this.http.post<{ records: Product[]; recordsCount: number }>(
      `${AppConfigService.settings.apiUrl}CustomProduct/ProductsList`,
      payload
    );
  }
  getRoles() {
    return this.http.get<{ id: string; name: string }[]>(
      `${AppConfigService.settings.apiUrl}Users/roles`
    );
  }
}
